import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Badge } from "../../components/ui";
import { Sheet, SheetBody, SheetFooter } from "../../components/ui/Sheet";
import {
  ArrowLeft,
  Shield,
  CheckCircle2,
  XCircle,
  Star,
  Info,
  ChevronRight,
  BarChart2,
  CreditCard,
  Check,
  AlertCircle,
  Loader2,
  Home,
  Zap,
  Wrench,
  FileText,
  Trash2,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { getAssetById, Asset } from "../../services/asset.api";
import {
  getPaymentMethods,
  attachPaymentMethod,
  setDefaultPaymentMethod,
  deletePaymentMethod,
  PaymentMethod,
} from "../../services/payment-methods.api";

// ── Types ─────────────────────────────────────────────────────────────────────

interface CoverageItem {
  label: string;
  covered: boolean;
  note?: string;
}

interface ProtectionPlan {
  id: string;
  provider: "301io" | "American Home Shield" | "Choice Home Warranty" | "Select Home Warranty" | "Liberty Home Guard" | "First American";
  planName: string;
  monthlyPrice: number;
  yearlyPrice: number;
  rating: number;
  reviewCount: number;
  serviceFee: number;
  maxCoveragePerItem: number;
  annualCoverageCap: number;
  waitingPeriodDays: number;
  contractTermMonths: number;
  coverageScope: "single-appliance" | "appliance-bundle" | "whole-home";
  highlights: string[];
  coverage: CoverageItem[];
  featured: boolean;
  tag?: string;
  homeIQ?: boolean;
  cancellationPolicy: string;
  renewalPolicy: string;
}

// ── Realistic US market pricing (2025-2026) ─────────────────────────────────

function buildPlans(category: string, assetAge: number, purchasePrice: number): ProtectionPlan[] {
  const cat = category.toLowerCase();
  const homeIQBase = getHomeIQBasePrice(cat, assetAge);
  const homeIQYearly = homeIQBase * 10; // 2 months free = 10x monthly
  const homeIQMaxCoverage = Math.min(Math.max(purchasePrice * 0.8, 500), 3500);

  return [
    {
      id: "homeiq-essential",
      provider: "301io",
      planName: "301io Essential",
      monthlyPrice: homeIQBase,
      yearlyPrice: homeIQYearly,
      rating: 4.6,
      reviewCount: 1840,
      serviceFee: 65,
      maxCoveragePerItem: homeIQMaxCoverage,
      annualCoverageCap: homeIQMaxCoverage,
      waitingPeriodDays: 0,
      contractTermMonths: 12,
      coverageScope: "single-appliance",
      featured: true,
      tag: "Recommended",
      homeIQ: true,
      cancellationPolicy: "Cancel anytime, prorated refund",
      renewalPolicy: "Auto-renews at same rate — locked in price",
      highlights: [
        "No waiting period — coverage starts today",
        "Dedicated 301io service network",
        "In-app claim filing in under 2 minutes",
        "Price locked — no surprise renewals",
      ],
      coverage: buildCoverage(cat, "homeiq"),
    },
    {
      id: "ahs-gold",
      provider: "American Home Shield",
      planName: "ShieldGold",
      monthlyPrice: 49.99,
      yearlyPrice: 499.99,
      rating: 4.3,
      reviewCount: 12400,
      serviceFee: 100,
      maxCoveragePerItem: 5000,
      annualCoverageCap: 10000,
      waitingPeriodDays: 30,
      contractTermMonths: 12,
      coverageScope: "whole-home",
      featured: true,
      tag: "Most Popular",
      homeIQ: false,
      cancellationPolicy: "Cancel within 30 days for full refund; $75 fee after",
      renewalPolicy: "Auto-renews; rates may increase annually",
      highlights: [
        "23 home systems & appliances covered",
        "Up to $5,000 per covered item",
        "Nationwide network (17,000+ contractors)",
        "No home inspection required",
      ],
      coverage: buildCoverage(cat, "ahs"),
    },
    {
      id: "choice-total",
      provider: "Choice Home Warranty",
      planName: "Total Plan",
      monthlyPrice: 54.17,
      yearlyPrice: 530,
      rating: 4.2,
      reviewCount: 8700,
      serviceFee: 85,
      maxCoveragePerItem: 3000,
      annualCoverageCap: 8000,
      waitingPeriodDays: 30,
      contractTermMonths: 12,
      coverageScope: "whole-home",
      featured: true,
      tag: "Best Value",
      homeIQ: false,
      cancellationPolicy: "30-day free look period; $50 admin fee after",
      renewalPolicy: "Auto-renews; first-month-free promo available",
      highlights: [
        "All major systems & appliances","24/7 claim filing online or by phone",
        "Covers pre-existing conditions","No age restrictions on appliances",
      ],
      coverage: buildCoverage(cat, "choice"),
    },
    {
      id: "select-platinum",
      provider: "Select Home Warranty",
      planName: "Platinum Care",
      monthlyPrice: 44.42,
      yearlyPrice: 420,
      rating: 4.0,
      reviewCount: 5200,
      serviceFee: 75,
      maxCoveragePerItem: 4000,
      annualCoverageCap: 9000,
      waitingPeriodDays: 30,
      contractTermMonths: 12,
      coverageScope: "whole-home",
      featured: false,
      homeIQ: false,
      cancellationPolicy: "Full refund if cancelled within 30 days",
      renewalPolicy: "Auto-renews; 2-month-free promo available",
      highlights: [
        "Unlimited service calls","Roof leak coverage included",
        "No age or condition limits","2 free months with annual plan",
      ],
      coverage: buildCoverage(cat, "select"),
    },
    {
      id: "liberty-total",
      provider: "Liberty Home Guard",
      planName: "Total Home Guard",
      monthlyPrice: 59.99,
      yearlyPrice: 575,
      rating: 4.4,
      reviewCount: 6100,
      serviceFee: 80,
      maxCoveragePerItem: 2000,
      annualCoverageCap: 8000,
      waitingPeriodDays: 30,
      contractTermMonths: 12,
      coverageScope: "whole-home",
      featured: false,
      homeIQ: false,
      cancellationPolicy: "Cancel within 30 days for full refund",
      renewalPolicy: "Auto-renews; optional multi-year lock-in",
      highlights: [
        "Appliances + all major home systems","Highly rated customer service (J.D. Power)",
        "Garage door opener & pest add-ons","Smart home device coverage",
      ],
      coverage: buildCoverage(cat, "liberty"),
    },
    {
      id: "first-american-premier",
      provider: "First American",
      planName: "Premier Plan",
      monthlyPrice: 47.50,
      yearlyPrice: 475,
      rating: 4.1,
      reviewCount: 4300,
      serviceFee: 75,
      maxCoveragePerItem: 3500,
      annualCoverageCap: 7500,
      waitingPeriodDays: 30,
      contractTermMonths: 12,
      coverageScope: "whole-home",
      featured: false,
      homeIQ: false,
      cancellationPolicy: "Prorated refund minus $30 admin fee",
      renewalPolicy: "Auto-renews at competitive rate",
      highlights: [
        "25+ years of warranty experience","Unlimited service calls included",
        "Covers systems + kitchen appliances","Transferable if you sell your home",
      ],
      coverage: buildCoverage(cat, "firstAmerican"),
    },
  ];
}

function getHomeIQBasePrice(cat: string, age: number): number {
  const ageSurcharge = Math.max(0, (age - 5) * 1);
  if (cat.includes("hvac") || cat.includes("air condition") || cat.includes("furnace") || cat.includes("heat pump"))
    return Math.round((28 + ageSurcharge) * 100) / 100;
  if (cat.includes("water heater")) return Math.round((16 + ageSurcharge) * 100) / 100;
  if (cat.includes("refrigerator") || cat.includes("fridge")) return Math.round((12 + ageSurcharge) * 100) / 100;
  if (cat.includes("washer") || cat.includes("dryer")) return Math.round((11 + ageSurcharge) * 100) / 100;
  if (cat.includes("dishwasher")) return Math.round((9 + ageSurcharge) * 100) / 100;
  if (cat.includes("oven") || cat.includes("range") || cat.includes("stove")) return Math.round((9 + ageSurcharge) * 100) / 100;
  if (cat.includes("microwave")) return Math.round((6 + ageSurcharge) * 100) / 100;
  return Math.round((10 + ageSurcharge) * 100) / 100;
}

function buildCoverage(cat: string, provider: string): CoverageItem[] {
  const isHvac = cat.includes("hvac") || cat.includes("air condition") || cat.includes("furnace") || cat.includes("heat pump");
  const items: CoverageItem[] = [
    { label: "Mechanical/electrical failures", covered: true },
    { label: "Parts & labor costs", covered: true },
    {
      label: "Repair or full replacement",
      covered: provider !== "choice",
      note: provider === "choice" ? "Repair only; replacement at provider discretion" : undefined,
    },
    {
      label: "Pre-existing conditions",
      covered: provider === "choice" || provider === "select",
      note: !["choice","select"].includes(provider) ? "Unknown pre-existing not covered" : undefined,
    },
    { label: "Cosmetic/external panels", covered: false, note: "Never covered by any provider" },
    { label: "Improper installation damage", covered: false },
    { label: "Manufacturer recall repairs", covered: false, note: "Covered under manufacturer recall" },
    {
      label: "Normal wear & aging",
      covered: provider !== "firstAmerican",
      note: provider === "firstAmerican" ? "Subject to inspection approval" : undefined,
    },
    { label: "24/7 emergency service", covered: ["ahs","homeiq","choice"].includes(provider) },
  ];
  if (isHvac) {
    items.push(
      { label: "Refrigerant recharge", covered: provider === "ahs" || provider === "homeiq" },
      { label: "Ductwork repairs", covered: provider === "ahs" },
      { label: "Thermostat included", covered: provider !== "select" }
    );
  }
  return items;
}

function formatCurrency(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}
function formatCurrencyDecimal(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

// ── Component ─────────────────────────────────────────────────────────────────

type ViewMode = "cards" | "compare";
type BillingCycle = "monthly" | "yearly";

export function WarrantyPlans() {
  const { t } = useTranslation("warranty");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const assetId = searchParams.get("assetId");
  const assetNameParam = searchParams.get("assetName");
  const categoryParam = searchParams.get("category");

  const [asset, setAsset] = useState<Asset | null>(null);
  const [assetLoading, setAssetLoading] = useState(!!assetId);
  const [viewMode, setViewMode] = useState<ViewMode>("cards");
  const [billing, setBilling] = useState<BillingCycle>("monthly");
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [detailPlan, setDetailPlan] = useState<ProtectionPlan | null>(null);
  const [purchasePlan, setPurchasePlan] = useState<ProtectionPlan | null>(null);

  useEffect(() => {
    if (!assetId) return;
    setAssetLoading(true);
    getAssetById(assetId)
      .then((d) => setAsset(d))
      .catch(() => setAsset(null))
      .finally(() => setAssetLoading(false));
  }, [assetId]);

  const assetName = asset?.name ?? assetNameParam ?? undefined;
  const category = asset?.category ?? categoryParam ?? "Appliance";

  const assetAge = useMemo(() => {
    if (!asset) return 3;
    const year = asset.purchaseYear || (asset.purchasedAt ? new Date(asset.purchasedAt).getFullYear() : null);
    if (!year) return 3;
    return new Date().getFullYear() - year;
  }, [asset]);

  const purchasePrice = asset?.purchasePrice ?? 800;
  const plans = useMemo(() => buildPlans(category, assetAge, purchasePrice), [category, assetAge, purchasePrice]);
  const featuredPlans = plans.filter((p) => p.featured);
  const otherPlans = plans.filter((p) => !p.featured);

  function toggleCompare(id: string) {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) return [...prev.slice(1), id];
      return [...prev, id];
    });
  }

  const _comparePlans = useMemo(() => plans.filter((p) => compareIds.includes(p.id)), [plans, compareIds]);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur shadow-md">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 h-14 flex items-center gap-3">
          <button onClick={() => navigate(assetId ? `/assets/${assetId}` : "/assets")}
            className="h-8 w-8 rounded-full border flex items-center justify-center flex-shrink-0 hover:bg-secondary transition-colors">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-sm font-medium truncate">{t("plans.header")}</h1>
            {assetName && (
              <p className="text-xs text-muted-foreground truncate">
                {assetName}{category ? ` · ${category}` : ""}
              </p>
            )}
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8">

        {/* Asset context */}
        {assetId && (
          <div className="rounded-xl border bg-card p-4 mb-6 flex items-center gap-4">
            <div className="h-10 w-10 rounded-lg border flex items-center justify-center flex-shrink-0">
              <Shield className="h-5 w-5" />
            </div>
            {assetLoading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{t("plans.loadingAsset")}</p>
              </div>
            ) : (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{assetName}</p>
                <p className="text-xs text-muted-foreground">
                  {category} · {assetAge > 0 ? t("plans.yearsOld", { years: assetAge }) : t("plans.ageUnknown")}
                  {asset?.brand ? ` · ${asset.brand}` : ""}
                  {purchasePrice > 0 ? ` · ${t("plans.purchasedFor", { price: formatCurrency(purchasePrice) })}` : ""}
                </p>
              </div>
            )}
          </div>
        )}

        <div className="mb-6">
          <h2 className="text-2xl font-light tracking-tight mb-2">{t("plans.title")}</h2>
          <p className="text-sm text-muted-foreground max-w-2xl leading-relaxed">
            {t("plans.subtitle")}
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex items-center border rounded-lg overflow-hidden flex-shrink-0">
            <button onClick={() => setViewMode("cards")}
              className={cn("px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5",
                viewMode === "cards" ? "bg-foreground text-background" : "hover:bg-secondary")}>
              <Home className="h-3 w-3" /> {t("plans.viewCards")}
            </button>
            <button onClick={() => setViewMode("compare")}
              className={cn("px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5",
                viewMode === "compare" ? "bg-foreground text-background" : "hover:bg-secondary")}>
              <BarChart2 className="h-3 w-3" /> {t("plans.viewCompare")}
              {compareIds.length > 0 && (
                <span className="ml-0.5 h-4 w-4 rounded-full bg-current text-background text-[9px] flex items-center justify-center font-bold">
                  {compareIds.length}
                </span>
              )}
            </button>
          </div>
          <div className="flex items-center border rounded-lg overflow-hidden flex-shrink-0">
            <button onClick={() => setBilling("monthly")}
              className={cn("px-3 py-1.5 text-xs font-medium transition-colors",
                billing === "monthly" ? "bg-foreground text-background" : "hover:bg-secondary")}>
              {t("plans.billingMonthly")}
            </button>
            <button onClick={() => setBilling("yearly")}
              className={cn("px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1",
                billing === "yearly" ? "bg-foreground text-background" : "hover:bg-secondary")}>
              {t("plans.billingAnnual")}
              <span className={cn("text-[9px] font-bold px-1 rounded-sm",
                billing === "yearly" ? "bg-background text-foreground" : "bg-green-100 text-green-700")}>
                {t("plans.save")}
              </span>
            </button>
          </div>
          {compareIds.length > 0 && viewMode === "cards" && (
            <Button size="sm" variant="outline" className="text-xs gap-1" onClick={() => setViewMode("compare")}>
              <BarChart2 className="h-3 w-3" /> {t("plans.viewCompared", { count: compareIds.length })}
            </Button>
          )}
          {viewMode === "cards" && compareIds.length === 0 && (
            <p className="text-xs text-muted-foreground">{t("plans.compareHint")}</p>
          )}
        </div>

        {/* Cards View */}
        {viewMode === "cards" && (
          <>
            <div className="mb-8">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-4">{t("plans.topRated")}</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {featuredPlans.map((plan) => (
                  <PlanCard key={plan.id} plan={plan} billing={billing}
                    isSelected={compareIds.includes(plan.id)}
                    onToggleCompare={() => toggleCompare(plan.id)}
                    onViewDetail={() => setDetailPlan(plan)}
                    onGetPlan={() => setPurchasePlan(plan)} />
                ))}
              </div>
            </div>
            {otherPlans.length > 0 && (
              <div className="mb-8">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-4">{t("plans.moreOptions")}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {otherPlans.map((plan) => (
                    <PlanCard key={plan.id} plan={plan} billing={billing}
                      isSelected={compareIds.includes(plan.id)}
                      onToggleCompare={() => toggleCompare(plan.id)}
                      onViewDetail={() => setDetailPlan(plan)}
                      onGetPlan={() => setPurchasePlan(plan)} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Compare View */}
        {viewMode === "compare" && (
          <CompareTable allPlans={plans} selectedIds={compareIds} billing={billing}
            onToggleCompare={toggleCompare}
            onGetPlan={(plan) => setPurchasePlan(plan)}
            onViewDetail={(plan) => setDetailPlan(plan)} />
        )}

        {/* How it works */}
        <div className="rounded-xl border bg-card p-6 mt-10">
          <h3 className="text-sm font-semibold mb-4">{t("plans.howTitle")}</h3>
          <div className="grid sm:grid-cols-3 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2"><Shield className="h-4 w-4" /><p className="text-sm font-medium">{t("plans.howStep1Title")}</p></div>
              <p className="text-xs text-muted-foreground leading-relaxed">{t("plans.howStep1Desc")}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2"><Wrench className="h-4 w-4" /><p className="text-sm font-medium">{t("plans.howStep2Title")}</p></div>
              <p className="text-xs text-muted-foreground leading-relaxed">{t("plans.howStep2Desc")}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2"><CreditCard className="h-4 w-4" /><p className="text-sm font-medium">{t("plans.howStep3Title")}</p></div>
              <p className="text-xs text-muted-foreground leading-relaxed">{t("plans.howStep3Desc")}</p>
            </div>
          </div>
        </div>

        <div className="flex items-start gap-2 mt-6 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <p>
            {t("plans.disclaimer")}
          </p>
        </div>
      </div>

      {detailPlan && (
      <PlanDetailDrawer plan={detailPlan} billing={billing} assetName={assetName}
        onClose={() => setDetailPlan(null)}
        onGetPlan={() => { setDetailPlan(null); setPurchasePlan(detailPlan); }} />
    )}
    {purchasePlan && (
      <PurchaseFlowDrawer plan={purchasePlan} billing={billing} assetName={assetName}
        _assetId={assetId ?? undefined} onClose={() => setPurchasePlan(null)} />
    )}
  </div>
);
}

// ── Plan Card ─────────────────────────────────────────────────────────────────

function PlanCard({ plan, billing, isSelected, onToggleCompare, onViewDetail, onGetPlan }: {
  plan: ProtectionPlan; billing: BillingCycle; isSelected: boolean;
  onToggleCompare: () => void; onViewDetail: () => void; onGetPlan: () => void;
}) {
  const { t } = useTranslation("warranty");
  const displayPrice = billing === "monthly" ? plan.monthlyPrice : plan.yearlyPrice / 12;
  const annualSavings = plan.monthlyPrice * 12 - plan.yearlyPrice;

  return (
    <div className={cn(
      "rounded-xl border bg-card p-5 relative flex flex-col transition-all",
      plan.homeIQ ? "ring-2 ring-foreground border-foreground" : "hover:border-foreground/20",
      isSelected && "ring-2 ring-blue-500"
    )}>
      {plan.tag && (
        <Badge className={cn("absolute top-4 right-4 text-[10px] font-medium", plan.homeIQ && "bg-foreground text-background")}>
          {plan.tag}
        </Badge>
      )}
      <div className="mb-3">
        <p className={cn("text-xs font-medium mb-0.5", plan.homeIQ ? "text-foreground" : "text-muted-foreground")}>
          {plan.homeIQ ? t("plans.homeIQProvider") : plan.provider}
        </p>
        <h4 className="text-base font-semibold leading-tight">{plan.planName}</h4>
        <p className="text-xs text-muted-foreground mt-0.5 capitalize">{plan.coverageScope.replace("-", " ")}</p>
      </div>

      <div className="mb-1">
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-semibold tabular-nums">{formatCurrencyDecimal(displayPrice)}</span>
          <span className="text-sm text-muted-foreground">{t("planCard.perMonth")}</span>
        </div>
        {billing === "yearly" && (
          <p className="text-xs text-muted-foreground">{t("plans.billedPerYear", { amount: formatCurrency(plan.yearlyPrice) })}</p>
        )}
        {billing === "yearly" && annualSavings > 2 && (
          <p className="text-xs text-green-600 font-medium">{t("plans.saveVsMonthly", { amount: formatCurrency(annualSavings) })}</p>
        )}
      </div>

      <div className="flex items-center gap-2 mb-4 mt-1">
        <div className="flex items-center gap-1">
          <Star className="h-3.5 w-3.5 fill-yellow-500 text-yellow-500" />
          <span className="text-sm font-medium tabular-nums">{plan.rating}</span>
        </div>
        <span className="text-xs text-muted-foreground">{t("plans.reviewsCount", { count: plan.reviewCount })}</span>
      </div>

      <ul className="space-y-2 mb-4 flex-1">
        {plan.highlights.map((h, i) => (
          <li key={i} className="flex items-start gap-2 text-xs">
            <CheckCircle2 className="h-3.5 w-3.5 text-foreground mt-0.5 flex-shrink-0" /><span>{h}</span>
          </li>
        ))}
      </ul>

      <div className="grid grid-cols-3 gap-2 mb-4 pt-3 border-t text-center">
        <div><p className="text-[10px] text-muted-foreground">{t("plans.serviceFeeLabel")}</p><p className="text-xs font-semibold">{formatCurrency(plan.serviceFee)}</p></div>
        <div><p className="text-[10px] text-muted-foreground">{t("plans.maxPerItem")}</p><p className="text-xs font-semibold">{formatCurrency(plan.maxCoveragePerItem)}</p></div>
        <div><p className="text-[10px] text-muted-foreground">{t("plans.wait")}</p><p className="text-xs font-semibold">{plan.waitingPeriodDays === 0 ? t("plans.none") : `${plan.waitingPeriodDays}d`}</p></div>
      </div>

      <div className="space-y-2">
        <Button variant={plan.homeIQ ? "default" : "outline"} size="sm" className="w-full text-xs" onClick={onGetPlan}>
          {plan.homeIQ ? t("plans.getThisPlan") : t("plans.getQuote")}
        </Button>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" className="flex-1 text-xs" onClick={onViewDetail}>{t("plans.viewDetails")}</Button>
          <button onClick={onToggleCompare} className={cn(
            "flex-1 text-xs px-2 py-1.5 rounded-md border font-medium transition-colors",
            isSelected ? "bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-950/30 dark:text-blue-300" : "hover:bg-secondary"
          )}>
            {isSelected ? t("plans.comparing") : t("plans.addToCompare")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Compare Table ─────────────────────────────────────────────────────────────

function CompareTable({ allPlans, selectedIds, billing, onToggleCompare, onGetPlan, onViewDetail }: {
  allPlans: ProtectionPlan[]; selectedIds: string[]; billing: BillingCycle;
  onToggleCompare: (id: string) => void; onGetPlan: (p: ProtectionPlan) => void; onViewDetail: (p: ProtectionPlan) => void;
}) {
  const { t } = useTranslation("warranty");
  const displayPlans = selectedIds.length > 0 ? allPlans.filter((p) => selectedIds.includes(p.id)) : allPlans.slice(0, 3);

  return (
    <div>
      {selectedIds.length === 0 && (
        <div className="rounded-xl border bg-card p-5 mb-6">
          <p className="text-sm font-medium mb-1">{t("plans.selectToCompare")}</p>
          <p className="text-xs text-muted-foreground mb-3">{t("plans.selectDesc")}</p>
          <div className="grid sm:grid-cols-3 gap-2">
            {allPlans.map((plan) => (
              <button key={plan.id} onClick={() => onToggleCompare(plan.id)}
                className={cn("rounded-lg border p-3 text-left transition-colors hover:bg-secondary",
                  selectedIds.includes(plan.id) && "ring-2 ring-blue-500")}>
                <p className="text-xs text-muted-foreground">{plan.homeIQ ? "301io" : plan.provider}</p>
                <p className="text-sm font-medium">{plan.planName}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatCurrencyDecimal(billing === "monthly" ? plan.monthlyPrice : plan.yearlyPrice / 12)}/mo
                </p>
              </button>
            ))}
          </div>
        </div>
      )}
      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-medium">{t("plans.comparingCount", { count: displayPlans.length })}</p>
          <Button variant="outline" size="sm" className="text-xs"
            onClick={() => displayPlans.forEach((p) => onToggleCompare(p.id))}>{t("plans.clear")}</Button>
        </div>
      )}
      <CompareGrid plans={displayPlans} billing={billing} onGetPlan={onGetPlan} onViewDetail={onViewDetail} />
    </div>
  );
}

function CompareGrid({ plans, billing, onGetPlan, onViewDetail }: {
  plans: ProtectionPlan[]; billing: BillingCycle;
  onGetPlan: (p: ProtectionPlan) => void; onViewDetail: (p: ProtectionPlan) => void;
}) {
  const { t } = useTranslation("warranty");
  const rows: Array<{ label: string; getValue: (p: ProtectionPlan) => React.ReactNode }> = [
    { label: t("plans.rowMonthlyPrice"), getValue: (p) => <span className="font-semibold text-sm tabular-nums">{formatCurrencyDecimal(billing === "monthly" ? p.monthlyPrice : p.yearlyPrice / 12)}/mo</span> },
    { label: t("plans.rowAnnualCost"), getValue: (p) => <span className="text-sm tabular-nums">{billing === "yearly" ? formatCurrency(p.yearlyPrice) : formatCurrency(p.monthlyPrice * 12)}</span> },
    { label: t("plans.rowServiceFee"), getValue: (p) => <span className="text-sm tabular-nums">{formatCurrency(p.serviceFee)}</span> },
    { label: t("plans.rowMaxCoverage"), getValue: (p) => <span className="text-sm tabular-nums">{formatCurrency(p.maxCoveragePerItem)}</span> },
    { label: t("plans.rowAnnualCap"), getValue: (p) => <span className="text-sm tabular-nums">{formatCurrency(p.annualCoverageCap)}</span> },
    { label: t("plans.rowWaiting"), getValue: (p) => <span className={cn("text-sm font-medium", p.waitingPeriodDays === 0 && "text-green-600")}>{p.waitingPeriodDays === 0 ? t("plans.noneCheck") : t("plans.waitDays", { count: p.waitingPeriodDays })}</span> },
    { label: t("plans.rowScope"), getValue: (p) => <span className="text-sm capitalize">{p.coverageScope.replace("-", " ")}</span> },
    { label: t("plans.rowRating"), getValue: (p) => <span className="flex items-center justify-center gap-1 text-sm"><Star className="h-3.5 w-3.5 fill-yellow-500 text-yellow-500" />{p.rating}</span> },
    { label: t("plans.rowContract"), getValue: (p) => <span className="text-sm">{t("plans.contractMonths", { count: p.contractTermMonths })}</span> },
    { label: t("plans.rowCancellation"), getValue: (p) => <span className="text-xs text-muted-foreground">{p.cancellationPolicy}</span> },
  ];

  const allCoverageLabels = Array.from(new Set(plans.flatMap((p) => p.coverage.map((c) => c.label))));

  return (
    <div className="overflow-x-auto rounded-xl border bg-card">
      <table className="w-full min-w-[480px]">
        <thead>
          <tr className="border-b">
            <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wide w-44">{t("plans.feature")}</th>
            {plans.map((plan) => (
              <th key={plan.id} className="p-4 text-center">
                <div className={cn("rounded-lg p-2", plan.homeIQ && "bg-foreground/5 ring-1 ring-foreground/20")}>
                  <p className="text-[10px] text-muted-foreground">{plan.homeIQ ? "301io" : plan.provider}</p>
                  <p className="text-sm font-semibold leading-tight">{plan.planName}</p>
                  {plan.tag && <Badge className="text-[9px] mt-1">{plan.tag}</Badge>}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label} className="border-b last:border-b-0 hover:bg-secondary/40 transition-colors">
              <td className="p-4 text-xs text-muted-foreground">{row.label}</td>
              {plans.map((plan) => <td key={plan.id} className="p-4 text-center">{row.getValue(plan)}</td>)}
            </tr>
          ))}
          <tr>
            <td colSpan={plans.length + 1} className="px-4 py-2 bg-secondary/30">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{t("plans.coverageDetails")}</p>
            </td>
          </tr>
          {allCoverageLabels.map((label) => (
            <tr key={label} className="border-b last:border-b-0 hover:bg-secondary/40 transition-colors">
              <td className="p-4 text-xs text-muted-foreground">{label}</td>
              {plans.map((plan) => {
                const item = plan.coverage.find((c) => c.label === label);
                return (
                  <td key={plan.id} className="p-4 text-center">
                    {!item ? <span className="text-muted-foreground text-xs">—</span>
                      : item.covered ? (
                        <div className="flex flex-col items-center gap-0.5">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          {item.note && <span className="text-[9px] text-muted-foreground max-w-[100px] leading-tight">{item.note}</span>}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-0.5">
                          <XCircle className="h-4 w-4 text-muted-foreground" />
                          {item.note && <span className="text-[9px] text-muted-foreground max-w-[100px] leading-tight">{item.note}</span>}
                        </div>
                      )}
                  </td>
                );
              })}
            </tr>
          ))}
          <tr className="bg-secondary/20">
            <td className="p-4" />
            {plans.map((plan) => (
              <td key={plan.id} className="p-4 text-center">
                <div className="space-y-2">
                  <Button size="sm" variant={plan.homeIQ ? "default" : "outline"} className="w-full text-xs" onClick={() => onGetPlan(plan)}>
                    {plan.homeIQ ? t("plans.getThisPlan") : t("plans.getQuote")}
                  </Button>
                  <button className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline" onClick={() => onViewDetail(plan)}>
                    {t("plans.viewDetails")}
                  </button>
                </div>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ── Plan Detail Drawer ────────────────────────────────────────────────────────

function PlanDetailDrawer({ plan, billing, assetName, onClose, onGetPlan }: {
  plan: ProtectionPlan; billing: BillingCycle; assetName?: string;
  onClose: () => void; onGetPlan: () => void;
}) {
  const { t } = useTranslation("warranty");
  const displayPrice = billing === "monthly" ? plan.monthlyPrice : plan.yearlyPrice / 12;
  const annualCost = billing === "yearly" ? plan.yearlyPrice : plan.monthlyPrice * 12;
  const annualSavings = plan.monthlyPrice * 12 - plan.yearlyPrice;

  return (
    <Sheet
      open={true}
      onClose={onClose}
      title={plan.planName}
      description={t("detail.subtitle", { provider: plan.homeIQ ? "301io" : plan.provider })}
      width="lg"
    >
      <SheetBody className="space-y-5 text-zinc-900">
        {assetName && (
          <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 flex items-center gap-3">
            <Shield className="h-4 w-4 text-zinc-600 flex-shrink-0" />
            <p className="text-sm text-zinc-900">{t("detail.protectionFor")} <span className="font-medium">{assetName}</span></p>
          </div>
        )}
        
        <div className="rounded-lg border border-zinc-200 bg-white p-4 space-y-3">
          <p className="text-xs font-medium text-zinc-600 uppercase tracking-wide">{t("detail.pricing")}</p>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-semibold tabular-nums text-zinc-900">{formatCurrencyDecimal(displayPrice)}</span>
            <span className="text-zinc-600">{t("planCard.perMonth")}</span>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-zinc-200 text-sm">
            <div>
              <p className="text-xs text-zinc-600">{t("detail.annualCost", { billing: billing === "monthly" ? t("plans.billingMonthly") : t("plans.billingAnnual") })}</p>
              <p className="font-medium tabular-nums text-zinc-900">{formatCurrency(annualCost)}</p>
            </div>
            {annualSavings > 2 && (
              <div>
                <p className="text-xs text-zinc-600">{t("detail.savings")}</p>
                <p className="font-medium text-green-600 tabular-nums">{formatCurrency(annualSavings)}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-zinc-600">{t("detail.serviceFeeClaim")}</p>
              <p className="font-medium tabular-nums text-zinc-900">{formatCurrency(plan.serviceFee)}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-600">{t("detail.maxCoverageItem")}</p>
              <p className="font-medium tabular-nums text-zinc-900">{formatCurrency(plan.maxCoveragePerItem)}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-600">{t("detail.annualCap")}</p>
              <p className="font-medium tabular-nums text-zinc-900">{formatCurrency(plan.annualCoverageCap)}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-600">{t("detail.waitingPeriod")}</p>
              <p className={cn("font-medium", plan.waitingPeriodDays === 0 ? "text-green-600" : "text-zinc-900")}>
                {plan.waitingPeriodDays === 0 ? t("detail.noneLabel") : t("detail.daysCount", { count: plan.waitingPeriodDays })}
              </p>
            </div>
            <div>
              <p className="text-xs text-zinc-600">{t("detail.contractTerm")}</p>
              <p className="font-medium text-zinc-900">{t("detail.monthsCount", { count: plan.contractTermMonths })}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-600">{t("detail.coverageScope")}</p>
              <p className="font-medium text-zinc-900 capitalize">{plan.coverageScope.replace("-", " ")}</p>
            </div>
          </div>
        </div>

        <div>
          <p className="text-xs font-medium text-zinc-600 uppercase tracking-wide mb-3">{t("detail.whatsCovered")}</p>
          <ul className="space-y-2">
            {plan.coverage.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                {item.covered
                  ? <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  : <XCircle className="h-4 w-4 text-zinc-400 flex-shrink-0 mt-0.5" />}
                <div className="min-w-0">
                  <p className={cn("text-sm", item.covered ? "text-zinc-900" : "text-zinc-600")}>{item.label}</p>
                  {item.note && <p className="text-xs text-zinc-600 mt-0.5">{item.note}</p>}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-xs font-medium text-zinc-600 uppercase tracking-wide mb-3">{t("detail.policies")}</p>
          <div className="space-y-3">
            <div className="rounded-lg border border-zinc-200 bg-white p-3">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="h-3.5 w-3.5 text-zinc-700" />
                <p className="text-xs font-medium text-zinc-900">{t("detail.cancellation")}</p>
              </div>
              <p className="text-xs text-zinc-600">{plan.cancellationPolicy}</p>
            </div>
            <div className="rounded-lg border border-zinc-200 bg-white p-3">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="h-3.5 w-3.5 text-zinc-700" />
                <p className="text-xs font-medium text-zinc-900">{t("detail.renewal")}</p>
              </div>
              <p className="text-xs text-zinc-600">{plan.renewalPolicy}</p>
            </div>
          </div>
        </div>

        <div>
          <p className="text-xs font-medium text-zinc-600 uppercase tracking-wide mb-3">{t("detail.highlights")}</p>
          <ul className="space-y-2">
            {plan.highlights.map((h, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-zinc-900">
                <CheckCircle2 className="h-4 w-4 text-zinc-900 flex-shrink-0 mt-0.5" />
                {h}
              </li>
            ))}
          </ul>
        </div>
      </SheetBody>

      <SheetFooter>
        <Button className="w-full" onClick={onGetPlan}>
          {plan.homeIQ ? t("plans.getThisPlan") : t("plans.getQuote")}
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </SheetFooter>
    </Sheet>
  );
}

// ── Purchase Flow Drawer ──────────────────────────────────────────────────────

type PurchaseStep = "confirm" | "payment" | "success";

function PurchaseFlowDrawer({ plan, billing, assetName, _assetId, onClose }: {
  plan: ProtectionPlan; billing: BillingCycle; assetName?: string;
  _assetId?: string; onClose: () => void;
}) {
  const { t } = useTranslation("warranty");
  const [step, setStep] = useState<PurchaseStep>("confirm");
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");
  const [nameOnCard, setNameOnCard] = useState("");
  const [zip, setZip] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveCard, setSaveCard] = useState(true);
  
  // Payment methods state
  const [savedCards, setSavedCards] = useState<PaymentMethod[]>([]);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const [loadingCards, setLoadingCards] = useState(true);

  const displayPrice = billing === "monthly" ? plan.monthlyPrice : plan.yearlyPrice;
  const billingLabel = billing === "monthly" ? t("purchase.perMonth") : t("purchase.perYear");

  // Load saved payment methods on mount
  useEffect(() => {
    loadPaymentMethods();
  }, []);

  async function loadPaymentMethods() {
    try {
      setLoadingCards(true);
      const methods = await getPaymentMethods();
      setSavedCards(methods.filter(m => m.isActive));
      if (methods.length > 0) {
        const defaultCard = methods.find(m => m.isDefault && m.isActive);
        setSelectedCardId(defaultCard?.id || methods.find(m => m.isActive)?.id || null);
      } else {
        setUseNewCard(true);
      }
    } catch (err) {
      console.error("Failed to load payment methods:", err);
      setUseNewCard(true);
    } finally {
      setLoadingCards(false);
    }
  }

  function formatCardInput(val: string) {
    return val.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
  }
  
  function formatExpiryInput(val: string) {
    const d = val.replace(/\D/g, "").slice(0, 4);
    return d.length >= 3 ? d.slice(0, 2) + "/" + d.slice(2) : d;
  }

  function detectCardBrand(num: string): string {
    const cleaned = num.replace(/\s/g, "");
    if (/^4/.test(cleaned)) return "visa";
    if (/^5[1-5]/.test(cleaned)) return "mastercard";
    if (/^3[47]/.test(cleaned)) return "amex";
    if (/^6(?:011|5)/.test(cleaned)) return "discover";
    return "card";
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};
    
    if (useNewCard) {
      if (cardNumber.replace(/\s/g, "").length !== 16) errs.cardNumber = t("purchase.errCardDigits");
      if (!/^\d{2}\/\d{2}$/.test(expiry)) errs.expiry = t("purchase.errExpFormat");
      else {
        const [mm, yy] = expiry.split("/").map(Number);
        if (mm < 1 || mm > 12) errs.expiry = t("purchase.errInvalidMonth");
        else if (new Date(2000 + yy, mm - 1) < new Date()) errs.expiry = t("purchase.errExpired");
      }
      if (cvv.length < 3) errs.cvv = t("purchase.errCvv");
      if (!nameOnCard.trim()) errs.nameOnCard = t("purchase.errRequired");
      if (!/^\d{5}$/.test(zip)) errs.zip = t("purchase.errZip");
    } else {
      if (!selectedCardId) errs.selectedCard = t("purchase.errSelectMethod");
    }
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit() {
    if (!validate()) return;
    setSubmitting(true);
    
    try {
      // If using new card and saveCard is true, save it
      if (useNewCard && saveCard) {
        const [mm, yy] = expiry.split("/").map(Number);
        const brand = detectCardBrand(cardNumber);
        const last4 = cardNumber.replace(/\s/g, "").slice(-4);
        
        // In production, you'd use Stripe.js here to get a payment method ID
        // For now, we'll simulate with a random ID
        const stripePaymentMethodId = `pm_sim_${Date.now()}`;
        
        await attachPaymentMethod({
          stripePaymentMethodId,
          brand,
          last4,
          expMonth: mm,
          expYear: 2000 + yy,
          billingName: nameOnCard,
          isDefault: savedCards.length === 0,
        });
      }
      
      // Simulate payment processing
      setTimeout(() => {
        setSubmitting(false);
        setStep("success");
      }, 1800);
    } catch (err) {
      console.error("Payment failed:", err);
      setSubmitting(false);
      setErrors({ submit: t("purchase.errPayment") });
    }
  }

  async function handleDeleteCard(cardId: string) {
    try {
      await deletePaymentMethod(cardId);
      await loadPaymentMethods();
    } catch (err) {
      console.error("Failed to delete card:", err);
    }
  }

  async function handleSetDefault(cardId: string) {
    try {
      await setDefaultPaymentMethod(cardId);
      await loadPaymentMethods();
    } catch (err) {
      console.error("Failed to set default card:", err);
    }
  }

  const externalUrls: Partial<Record<string, string>> = {
    "American Home Shield": "https://www.ahs.com/",
    "Choice Home Warranty": "https://www.choicehomewarranty.com/",
    "Select Home Warranty": "https://www.selecthomewarranty.com/",
    "Liberty Home Guard": "https://libertyhomeguard.com/",
    "First American": "https://homewarranty.firstam.com/",
  };

  return (
    <Sheet
      open={true}
      onClose={step === "success" ? () => {} : onClose}
      title={step === "success" ? t("purchase.activated") : t("purchase.getProtection")}
      description={
        step === "confirm"
          ? t("purchase.step1")
          : step === "payment"
          ? t("purchase.step2")
          : t("purchase.step3")
      }
      width="md"
    >
      {step !== "success" && (
        <div className="flex gap-1 px-6 pt-3 border-b pb-3">
          {(["confirm", "payment"] as const).map((s, i) => (
            <div
              key={s}
              className={cn(
                "h-1 flex-1 rounded-full transition-colors",
                (step === "confirm" && i === 0) || step === "payment"
                  ? "bg-zinc-900"
                  : "bg-zinc-200"
              )}
            />
          ))}
        </div>
      )}

      <SheetBody>
        {step === "confirm" && (
          <div className="space-y-4">
            {assetName && (
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3">
                <p className="text-xs text-zinc-600">{t("purchase.protecting")}</p>
                <p className="text-sm font-medium text-zinc-900">{assetName}</p>
              </div>
            )}
            
            <div className="rounded-lg border border-zinc-200 bg-white p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-zinc-600">{plan.homeIQ ? "301io" : plan.provider}</p>
                  <p className="text-sm font-semibold text-zinc-900">{plan.planName}</p>
                </div>
                {plan.tag && <Badge className="text-[10px]">{plan.tag}</Badge>}
              </div>
              <div className="flex items-baseline gap-2 pt-2 border-t border-zinc-200">
                <span className="text-2xl font-semibold tabular-nums text-zinc-900">
                  {formatCurrencyDecimal(displayPrice)}
                </span>
                <span className="text-sm text-zinc-600">{billingLabel}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-zinc-600">{t("purchase.serviceFee")}</p>
                  <p className="font-medium text-zinc-900">{t("purchase.perClaim", { amount: formatCurrency(plan.serviceFee) })}</p>
                </div>
                <div>
                  <p className="text-zinc-600">{t("purchase.maxCoverage")}</p>
                  <p className="font-medium text-zinc-900">{formatCurrency(plan.maxCoveragePerItem)}</p>
                </div>
                <div>
                  <p className="text-zinc-600">{t("purchase.waitingPeriod")}</p>
                  <p className={cn("font-medium", plan.waitingPeriodDays === 0 ? "text-green-600" : "text-zinc-900")}>
                    {plan.waitingPeriodDays === 0 ? t("purchase.noneStartsToday") : t("purchase.daysCount", { count: plan.waitingPeriodDays })}
                  </p>
                </div>
                <div>
                  <p className="text-zinc-600">{t("purchase.contractTerm")}</p>
                  <p className="font-medium text-zinc-900">{t("purchase.monthsCount", { count: plan.contractTermMonths })}</p>
                </div>
              </div>
            </div>
            
            <ul className="space-y-1.5">
              {plan.highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-zinc-900">
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-600 flex-shrink-0 mt-0.5" />
                  {h}
                </li>
              ))}
            </ul>
            
            {!plan.homeIQ && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-900 leading-relaxed">
                  {t("purchase.externalNotice", { provider: plan.provider })}
                </p>
              </div>
            )}
          </div>
        )}

        {step === "payment" && (
          <div className="space-y-4">
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-600">{plan.planName}</p>
                {assetName && <p className="text-xs text-zinc-600">{assetName}</p>}
              </div>
              <p className="text-sm font-semibold tabular-nums text-zinc-900">
                {formatCurrencyDecimal(displayPrice)}/{billing === "monthly" ? "mo" : "yr"}
              </p>
            </div>

            {loadingCards ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
              </div>
            ) : (
              <>
                {savedCards.length > 0 && !useNewCard && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium text-zinc-900">{t("purchase.savedMethods")}</label>
                      <button
                        onClick={() => setUseNewCard(true)}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        {t("purchase.addNewCard")}
                      </button>
                    </div>
                    
                    {savedCards.map((card) => (
                      <div
                        key={card.id}
                        className={cn(
                          "rounded-lg border px-4 py-3 cursor-pointer transition-all",
                          selectedCardId === card.id
                            ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200"
                            : "border-zinc-200 bg-white hover:border-zinc-300"
                        )}
                        onClick={() => setSelectedCardId(card.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <CreditCard className="h-4 w-4 text-zinc-600" />
                            <div>
                              <p className="text-sm font-medium text-zinc-900 capitalize">
                                {card.brand} •••• {card.last4}
                              </p>
                              <p className="text-xs text-zinc-600">
                                {t("purchase.expires", { date: `${String(card.expMonth).padStart(2, "0")}/${card.expYear % 100}` })}
                                {card.isDefault && <span className="ml-2 text-blue-600 font-medium">{t("purchase.defaultBadge")}</span>}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {!card.isDefault && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSetDefault(card.id);
                                }}
                                className="p-1 hover:bg-zinc-100 rounded"
                                title={t("purchase.setDefault")}
                              >
                                <Star className="h-3.5 w-3.5 text-zinc-400" />
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteCard(card.id);
                              }}
                              className="p-1 hover:bg-red-50 rounded text-red-600"
                              title={t("purchase.deleteCard")}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {errors.selectedCard && (
                      <p className="text-xs text-red-500">{errors.selectedCard}</p>
                    )}
                  </div>
                )}

                {(useNewCard || savedCards.length === 0) && (
                  <div className="space-y-3">
                    {savedCards.length > 0 && (
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-medium text-zinc-900">{t("purchase.newCardDetails")}</label>
                        <button
                          onClick={() => {
                            setUseNewCard(false);
                            setCardNumber("");
                            setExpiry("");
                            setCvv("");
                            setNameOnCard("");
                            setZip("");
                          }}
                          className="text-xs text-zinc-600 hover:text-zinc-700"
                        >
                          {t("purchase.cancel")}
                        </button>
                      </div>
                    )}
                    
                    <div>
                      <label className="text-xs font-medium mb-1 block text-zinc-900">{t("purchase.nameOnCard")}</label>
                      <input
                        className={cn(
                          "w-full border rounded-lg px-3 py-2 text-sm bg-white text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500",
                          errors.nameOnCard && "border-red-500"
                        )}
                        placeholder={t("purchase.namePlaceholder")}
                        value={nameOnCard}
                        onChange={(e) => setNameOnCard(e.target.value)}
                      />
                      {errors.nameOnCard && <p className="text-xs text-red-500 mt-1">{errors.nameOnCard}</p>}
                    </div>
                    
                    <div>
                      <label className="text-xs font-medium mb-1 block text-zinc-900">{t("purchase.cardNumber")}</label>
                      <div className="relative">
                        <input
                          className={cn(
                            "w-full border rounded-lg px-3 py-2 text-sm bg-white text-zinc-900 placeholder:text-zinc-400 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500",
                            errors.cardNumber && "border-red-500"
                          )}
                          placeholder={t("purchase.cardNumberPlaceholder")}
                          value={cardNumber}
                          onChange={(e) => setCardNumber(formatCardInput(e.target.value))}
                          maxLength={19}
                        />
                        <CreditCard className="absolute right-3 top-2.5 h-4 w-4 text-zinc-400" />
                      </div>
                      {errors.cardNumber && <p className="text-xs text-red-500 mt-1">{errors.cardNumber}</p>}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium mb-1 block text-zinc-900">{t("purchase.expiration")}</label>
                        <input
                          className={cn(
                            "w-full border rounded-lg px-3 py-2 text-sm bg-white text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500",
                            errors.expiry && "border-red-500"
                          )}
                          placeholder={t("purchase.expiryPlaceholder")}
                          value={expiry}
                          onChange={(e) => setExpiry(formatExpiryInput(e.target.value))}
                          maxLength={5}
                        />
                        {errors.expiry && <p className="text-xs text-red-500 mt-1">{errors.expiry}</p>}
                      </div>
                      <div>
                        <label className="text-xs font-medium mb-1 block text-zinc-900">{t("purchase.cvv")}</label>
                        <input
                          className={cn(
                            "w-full border rounded-lg px-3 py-2 text-sm bg-white text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500",
                            errors.cvv && "border-red-500"
                          )}
                          placeholder={t("purchase.cvvPlaceholder")}
                          value={cvv}
                          onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
                          maxLength={4}
                        />
                        {errors.cvv && <p className="text-xs text-red-500 mt-1">{errors.cvv}</p>}
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-xs font-medium mb-1 block text-zinc-900">{t("purchase.zip")}</label>
                      <input
                        className={cn(
                          "w-full border rounded-lg px-3 py-2 text-sm bg-white text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500",
                          errors.zip && "border-red-500"
                        )}
                        placeholder={t("purchase.zipPlaceholder")}
                        value={zip}
                        onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                        maxLength={5}
                      />
                      {errors.zip && <p className="text-xs text-red-500 mt-1">{errors.zip}</p>}
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={saveCard}
                        onChange={(e) => setSaveCard(e.target.checked)}
                        className="rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-xs text-zinc-700">{t("purchase.saveCard")}</span>
                    </label>
                  </div>
                )}
              </>
            )}

            <div className="flex items-center gap-2 text-xs text-zinc-600 pt-2 border-t border-zinc-200">
              <Shield className="h-3.5 w-3.5" />
              <span>{t("purchase.secureNotice")}</span>
            </div>

            {errors.submit && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                <p className="text-xs text-red-900">{errors.submit}</p>
              </div>
            )}
          </div>
        )}

        {step === "success" && (
          <div className="py-8 text-center space-y-4">
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-zinc-900">{t("purchase.youreProtected")}</h4>
              <p className="text-sm text-zinc-600 mt-1">
                {assetName
                  ? t("purchase.planActiveForAsset", { plan: plan.planName, asset: assetName })
                  : t("purchase.planActive", { plan: plan.planName })}
              </p>
            </div>
            <div className="rounded-lg border border-zinc-200 bg-white p-4 text-left space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-600">{t("purchase.planLabel")}</span>
                <span className="font-medium text-zinc-900">{plan.planName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-600">{t("purchase.billingLabel")}</span>
                <span className="font-medium text-zinc-900">
                  {formatCurrencyDecimal(displayPrice)}/{billing === "monthly" ? "mo" : "yr"}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-600">{t("purchase.coverageStarts")}</span>
                <span className={cn("font-medium", plan.waitingPeriodDays === 0 ? "text-green-600" : "text-zinc-900")}>
                  {plan.waitingPeriodDays === 0
                    ? t("purchase.today")
                    : new Date(Date.now() + plan.waitingPeriodDays * 86400000).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-600">{t("purchase.serviceFee")}</span>
                <span className="font-medium text-zinc-900">{t("purchase.perClaim", { amount: formatCurrency(plan.serviceFee) })}</span>
              </div>
            </div>
            <p className="text-xs text-zinc-600">
              {t("purchase.confirmationEmail")}
            </p>
          </div>
        )}
      </SheetBody>

      {step === "confirm" && (
        <SheetFooter>
          <Button
            className="w-full"
            onClick={() => {
              if (!plan.homeIQ) {
                window.open(externalUrls[plan.provider] ?? "https://google.com", "_blank", "noopener,noreferrer");
              } else {
                setStep("payment");
              }
            }}
          >
            {plan.homeIQ ? t("purchase.continueToPayment") : t("purchase.goToProvider", { provider: plan.provider })}
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </SheetFooter>
      )}

      {step === "payment" && (
        <SheetFooter>
          <div className="flex gap-3 w-full">
            <Button variant="outline" size="sm" className="flex-1" onClick={() => setStep("confirm")}>
              {t("purchase.back")}
            </Button>
            <Button size="sm" className="flex-1" onClick={handleSubmit} disabled={submitting || loadingCards}>
              {submitting ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  {t("purchase.processing")}
                </>
              ) : (
                t("purchase.pay", { amount: formatCurrencyDecimal(displayPrice) })
              )}
            </Button>
          </div>
        </SheetFooter>
      )}

      {step === "success" && (
        <SheetFooter>
          <Button className="w-full" onClick={onClose}>
            {t("purchase.done")}
          </Button>
        </SheetFooter>
      )}    </Sheet>
  );
}
